import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { Edit } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { IndividualInvestment, InvestmentApiResponse } from "../../../types.js";
import { format } from "date-fns";
import { investmentDateFormat } from "../InvestmentInfoFields.js";
import { useRequest } from "../../../../../hooks/useRequest.js";
import routes from "../../../../../routes.js";

interface EditNoteProps {
  investment: IndividualInvestment;
  projectName: InvestmentApiResponse["projectName"];
  refreshInvestmentList: () => Promise<void>;
}

export const EditNote: React.FC<EditNoteProps> = ({
  investment,
  projectName,
  refreshInvestmentList,
}) => {
  const title = investment?.note ? "Edit note" : "Add note";
  const { fetch: uploadNote, error, loading } = useRequest();
  const [open, setOpen] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const result = await uploadNote(
      "PATCH",
      routes.api.investments.updateNote({
        investmentId: investment.investmentId,
      }),
      {
        note: formData.get("note"),
      },
    );

    if (result) {
      await refreshInvestmentList();
      setOpen(false);
    }
  };

  return (
    <React.Fragment>
      <Button
        onClick={() => {
          setOpen(true);
        }}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: 0,
        }}
      >
        <Typography>{title}</Typography>
        <Edit sx={{ marginLeft: 1 }} />
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: "md",
            },
          },
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              id="note"
              name="note"
              label={`${title} for ${projectName} ${format(new Date(investment.createdAt), investmentDateFormat)}`}
              inputMode="text"
              style={{}}
              type="textarea"
              fullWidth
              multiline
              rows={4}
              sx={{ mt: 2 }}
              defaultValue={investment.note}
              error={Boolean(error)}
              helperText={
                error
                  ? "An error occurred while updating the note. Please try again."
                  : undefined
              }
              slotProps={{
                htmlInput: { maxLength: 300 },
                inputLabel: {
                  sx: (theme) => ({
                    backgroundColor: theme.palette.background.default,
                    paddingRight: theme.spacing(1),
                  }),
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button loading={loading} type="submit">
              Confirm
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </React.Fragment>
  );
};
